/**
 * photos-edit.js
 */

import React from 'react';
import { DndProvider, useDrop } from 'react-dnd';
import { HTML5Backend, NativeTypes } from 'react-dnd-html5-backend';
import makeStyles from '@mui/styles/makeStyles';
import {
  alpha,
  Typography,
  List,
  ListItem,
  ListItemSecondaryAction,
  IconButton,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useFormContext, Controller } from 'react-hook-form';
import { MAX_REQUEST_SIZE_BYTES } from "utils/constants";
import Icon from 'components/icon';
import { EditorIconSection } from 'components/icon-section/icon-section';
import HelpTextItem from 'components/menu-drawer/help-text-item';
import FormSectionTitle from 'components/menu-drawer/form-section-title';
import FileResizer from 'react-image-file-resizer';



const useStyles = makeStyles(theme => ({
  dropContainer: {
    padding: `${theme.spacing(1)} ${theme.spacing(1)}`,
    transition: `${theme.transitions.duration.short}ms ${theme.transitions.easing.easeOut}`,
    outline: 'none',
    border: `dashed 4px ${theme.palette.grey.colors[7]}`,
    borderRadius: '5px',
    flexDirection: 'column',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  dragOver: {
    background: theme.palette.grey.colors[8],
    border: `dashed 4px ${theme.palette.grey.colors[5]}`,
    cursor: 'grab',
  },
  item: {
    backgroundColor: alpha(theme.palette.grey.colors[0], 0.05),
    margin: `${theme.spacing(0.5)} 0`,
    borderRadius: 4,
  },
  imgPreview: {
    height: theme.spacing(10),
    width: 'auto'
  }
}));


function PhotosEditContent(props) {
  const {
    value,
    onChange,
    mode,
  } = props;
  const classes = useStyles();
  const fileInput = React.useRef();
  const [error, setError] = React.useState();
  const [files, setFiles] = React.useState([]);
  const MAX_WIDTH = 3000;
  const MAX_HEIGHT = 3000;
  const COMPRESSION_QUALITY = 85; // between 0 - 100

  // update file list when value prop changes
  React.useEffect(() => {
    setFiles(value
      ? mode === 'single'
        ? [{ ...value, key: value.key || value.id }]
        : value.map(f => ({ ...f, key: f.key || f.id }))
      : []);
  }, [value, mode]);

  const compressImage = async (file) => {
    return new Promise(resolve => {
      FileResizer.imageFileResizer(file, MAX_WIDTH, MAX_HEIGHT, 'JPEG', COMPRESSION_QUALITY, 0, (resizedFile) => {
        resolve(resizedFile);
      }, 'file');
    });
  }

  const processFiles = async (fileList) => {
    const filesToCompress = [];
    let newFiles = [];

    if (mode === 'single') {
      // SINGLE MODE
      const f = fileList[0];
      const newFile = {
        key: `${f.name}-${f.size}`,
        file: f,
      };

      newFiles = [newFile];
      filesToCompress.push(newFile);
    } else {
      // MULTIPLE MODE
      newFiles = [...files];
      fileList.forEach(f => {
        const key = `${f.name}-${f.size}`;
        if (!newFiles.find(ff => ff.key === key) && f.type.includes("image")) {
          const newFile = {
            key,
            file: f
          };
          newFiles.push(newFile);
          filesToCompress.push(newFile);
        }
      });
    }

    const compressPromises = filesToCompress.map(async f => {
      try {
        const compressed = await compressImage(f.file);
        return {
          ...f,
          file: compressed,
        };
      } catch (e) {
        console.error(e);
        return f;
      }
    });

    const compressedFiles = await Promise.all(compressPromises);
    const processedFiles = newFiles.map(f => {
      const compressed = compressedFiles.find(c => c.key === f.key);
      return compressed ?? f;
    });

    // scan all files to check for oversize limit
    if (calculateUploadSize(processedFiles) > MAX_REQUEST_SIZE_BYTES) {
      setError(<>
        Your selected image(s) exceed the maximum size limit of 30MB. <br />
        Try using smaller image sizes if possible or upload one at a time.
      </>);
      return;
    }

    setError();
    setFiles(processedFiles);
    onChange(mode === 'single' ? processedFiles[0] : processedFiles);
  }


  const calculateUploadSize = (fileList) => {
    let size = fileList.reduce((sum, file) => {
      return !file.file 
        ? sum
        : sum + file.file.size
    }, 0);

    return size;
  }


  const onFileChanged = () => {
    processFiles(Array.from(fileInput.current.files));
  }


  const removeFile = (file) => {
    const newFiles = files.filter(f => f.key !== file.key);
    onChange((mode === 'single' && newFiles.length === 0) ? null : newFiles);
  }
  

  const [{ isOver, canDrop }, drop] = useDrop({
    accept: [NativeTypes.FILE],
    drop: (items) => processFiles(items.files),
    canDrop: (item) => {
      if (item.files && item.files.length > 0) {        
        const allImages = !Boolean(
          item.files.find(f => {
            return !f.type.includes("image");
          })
        );

        if (!allImages) {
          setError(<>
            One or more of your dropped files are not image files.<br/>
            Please select only image file types.
          </>)
        } else {
          setError();
        }

        return allImages;
      }

      return false;
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });


  return <>

    {/* drop area */}
    <div ref={drop}>
      <input 
        ref={fileInput}
        type="file"
        multiple={mode !== 'single'}
        accept="image/*"
        onChange={onFileChanged}
        style={{ display: 'none' }}
      />
      <div           
        onClick={() => fileInput.current.click()}
        className={`flex-hc ${classes.dropContainer} ${isOver && canDrop ? classes.dragOver : ''}`}
      >
        <Typography variant="subtitle1">Drop Photo{`${mode === 'single' ? '' : 's'}`} Here</Typography>
        <Typography variant="subtitle2" style={{ lineHeight: 0.8 }}>or</Typography>
        <Typography variant="subtitle1">Click To Select</Typography>
        {mode === 'single' && 
        <Typography variant='caption'>(Max of 1 Photo Only)</Typography>}
        {error &&
        <Typography color="error" style={{ marginTop: 16, textAlign: 'center' }}>{error}</Typography>}
      </div>
    </div>

    {/* file list */}
    <List style={{ paddingBottom: 0 }}>
      {files.map(file => 
        <ListItem
          key={file.key}
          className={classes.item}
        >
          <img 
            src={file.url || URL.createObjectURL(file.file)} 
            alt={file.key}
            className={classes.imgPreview}
          />
          <ListItemSecondaryAction>
            <IconButton onClick={() => removeFile(file)} size="large">
              <Icon type='delete' />
            </IconButton>
          </ListItemSecondaryAction>
        </ListItem>)}
    </List>

  </>;
}


export default function PhotosEdit(props) {
  const {
    formContext: formContextProp,
    mode,
    hideIcon,
  } = props;
  const { t } = useTranslation(props.translation);
  const formContextDefault = useFormContext();
  const formContext = formContextProp || formContextDefault;
  const { control } = formContext;


  const core = (
    <Controller
      control={control}
      name={props.name}
      defaultValue={props.defaultValue}
      render={( { field }) =>
        <DndProvider backend={HTML5Backend}>
          <PhotosEditContent
            mode={mode}
            value={field.value}
            onChange={field.onChange}
          />
        </DndProvider>}
    />
  );


  return hideIcon
    ? core
    : (
    <EditorIconSection
      iconName='photo'
      helpProps={{
        translation: props.translation,
        section: 'photos'
      }}
    >
      <HelpTextItem
        sidebarText={props.drawerState.sidebarText}
        title={t('photos.general.helpTitle')}
        text={t('photos.general.helpInfo')}
      >
        <FormSectionTitle style={{ marginTop: 8, marginBottom: 16 }}>{t('photos.sectionName')}</FormSectionTitle>
        {core}
      </HelpTextItem>
    </EditorIconSection>
  );
}